import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import {
  X, Tag, User, Search, ChevronDown, Building2, Users, UserCog, Check, Radio, FileText, Paperclip, ListChecks, Flag,
  MessageSquarePlus, MessageSquare, Network, Copy, Type, AlignLeft, SlidersHorizontal, StickyNote, Clock, Phone, Layers, type LucideIcon,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { api } from '../../api/client.js'
import { useCampaigns } from '../../hooks/useCampaigns.js'
import { useTeams } from '../../hooks/useTeams.js'
import { useAgentUsers } from '../../hooks/useAgentUsers.js'
import { usePriorities } from '../../hooks/usePriorities.js'
import { useTypes } from '../../hooks/useTypes.js'
import { useCategories } from '../../hooks/useCategories.js'
import { useSubcategories } from '../../hooks/useSubcategories.js'
import { useConversationStatuses } from '../../hooks/useConversationStatuses.js'
import { useStates } from '../../hooks/useStates.js'
import { useImpacts } from '../../hooks/useImpacts.js'
import { useUrgencies } from '../../hooks/useUrgencies.js'
import { useComplexities } from '../../hooks/useComplexities.js'
import { useLocations } from '../../hooks/useLocations.js'
import { useBranches } from '../../hooks/useBranches.js'
import { useDepartments } from '../../hooks/useDepartments.js'
import { useAdditionalProperties, type AdditionalProperty, type PropertyOption } from '../../hooks/useAdditionalProperties.js'
import { useConversationForms } from '../../hooks/useConversationForms.js'
import { useUploadAttachment } from '../../hooks/useUploadAttachment.js'
import { useCallStore } from '../../store/callStore.js'
import RichTextEditor, { isEmptyHtml } from '../common/RichTextEditor.js'
import { isValidEmail } from '../common/EmailChipsInput.js'
import { DrawerInfoButton, DrawerInfoPanel, useDrawerInfo } from '../ui/DrawerInfo.js'
import DatePicker from '../ui/DatePicker.js'

// Channel is fixed to the web agent channel — the agent portal only creates
// web-originated interactions here, so there is no channel picker. The "source"
// of the conversation is likewise fixed (mirrors source_id in the KC2 reference
// form, which is hardcoded for agent-created interactions).
const FIXED_CHANNEL = 'web' as const
// Source of the conversation. Agent-created conversations come from the agent
// portal (mirrors the KC2 reference where source_id = AGENT_PORTAL = 1).
const FIXED_SOURCE = 'AGENT_PORTAL' as const

/** "a@x.com, b@y.com; c@z.com" → trimmed, non-empty entries. */
function splitAddresses(value: string): string[] {
  return value.split(/[,;\s]+/).map((a) => a.trim()).filter(Boolean)
}

const SOURCE_LABEL = 'Agent Portal'

export interface NewConversationData {
  channel: typeof FIXED_CHANNEL
  source: string
  conversationFormId: string | undefined
  clientId: string | undefined
  customerName: string
  cc: string
  bcc: string
  callUuid: string
  campaignId: string
  teamId: string
  queueId: string | undefined
  agentId: string | undefined
  subject: string
  /** HTML from the rich text description editor. */
  message: string
  priorityId: string | undefined
  typeId?: string | undefined
  categoryId?: string | undefined
  subcategoryId?: string | undefined
  statusId?: string | undefined
  stateId?: string | undefined
  impactId?: string | undefined
  urgencyId?: string | undefined
  complexityId?: string | undefined
  locationId?: string | undefined
  branchId?: string | undefined
  departmentId?: string | undefined
  tags: string[]
  assignedTo: string | undefined
  additionalProperties: Record<string, unknown>
  attachments: File[]
}

/**
 * Parent context passed when this panel is opened in "child conversation" mode.
 * Presence of `parent` switches the panel into child mode: a parent banner +
 * a "Copy from Parent" section appear, and the created conversation is linked
 * to the parent (parentId + isParent:false). Mirrors the KC2 child-interaction
 * flow, which reuses the same interaction form for new + child.
 */
export interface ChildParentContext {
  conversationId: string
  referenceId?: string | null
  subject?: string | null
  /** HTML description of the parent (rich-text). */
  description?: string | null
  clientId?: string | null
  clientName?: string | null
  clientEmail?: string | null
  campaignId?: string | null
  /** assignedTeamId of the parent. */
  teamId?: string | null
  priorityId?: string | null
  tags?: string[] | undefined
}

/** "Copy from Parent" selections. Field copies pre-fill the form (frontend);
 *  attachment/checklist copies are performed by the API after create. */
interface CopyFromParentOptions {
  copySubject: boolean
  copyDescription: boolean
  copyClient: boolean
  copyCampaignTeam: boolean
  copyPriority: boolean
  copyTags: boolean
  copyAttachments: boolean
  copyChecklists: boolean
  copyAdditionalProperties: boolean
  copyGlobalNotes: boolean
  copyTimeline: boolean
}

const DEFAULT_COPY_OPTIONS: CopyFromParentOptions = {
  copySubject: false,
  copyDescription: false,
  copyClient: false,
  copyCampaignTeam: false,
  copyPriority: false,
  copyTags: false,
  copyAttachments: false,
  copyChecklists: false,
  copyAdditionalProperties: false,
  copyGlobalNotes: false,
  copyTimeline: false,
}

interface CreateConversationPanelProps {
  open: boolean
  onClose: () => void
  onCreate: (data: NewConversationData) => void
  /** When set, the panel creates a CHILD of this conversation. */
  parent?: ChildParentContext | undefined
  /** Child mode: called with the new child's id after a successful create. */
  onChildCreated?: ((childConversationId: string) => void) | undefined
}

// A client row from GET /admin/clients (the `clients` table). full_name is
// the primary human-facing label — falls back to email when not set.
interface Client {
  id: string
  email: string | null
  full_name?: string | null
}

// Subset of the conversation-form detail we need to decide which extra fields
// to render (GET /admin/conversation-management/forms/:id).
interface FormDetail {
  selectedProperties: string[]
  interactionProperties?: string[]
  showSubject: boolean
  showDescription: boolean
  formPropertiesOrder?: string[]
  formSeparators?: string
  propertyDefaults?: Record<string, string>
}

// A separator row parsed from FormDetail.formSeparators (JSON [{id,label}]).
interface Separator { id: string; label: string }

// One item in the ordered conversation-field stream (core fields, additional
// properties, fixed properties, or section separators) as defined by the form's formPropertiesOrder.
type OrderedItem =
  | { type: 'prop'; prop: AdditionalProperty }
  | { type: 'separator'; label: string }
  | { type: 'core'; key: 'subject' | 'description' }
  | { type: 'fixed'; key: string; label: string }

const FIXED_FIELD_LABELS: Record<string, string> = {
  priority_id: 'Priority',
  type_id: 'Type',
  category_id: 'Category',
  subcategory_id: 'Subcategory',
  status_id: 'Status',
  state_id: 'State',
  impact_id: 'Impact',
  urgency_id: 'Urgency',
  complexity_id: 'Complexity',
  location_id: 'Location',
  branch_id: 'Branch',
  department_id: 'Department',
}

// 3px border radius throughout the form (rounded-[3px]).
const inputClass =
  'w-full rounded-[3px] border bg-slate-50/70 px-3.5 py-2.5 text-sm text-slate-800 outline-none transition-all placeholder:text-slate-400 hover:bg-slate-50 focus:border-[#3366FF] focus:bg-white focus:ring-2 focus:ring-[#EEF4FF]'

/* ── Section wrapper: icon + title header, then a padded body ── */
function FormSection({
  icon: Icon,
  title,
  hint,
  children,
}: {
  icon: LucideIcon
  title: string
  hint?: string
  children: ReactNode
}): JSX.Element {
  return (
    <section className="overflow-hidden rounded-[3px] border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-[3px] bg-blue-50 text-[#3366FF]">
          <Icon className="h-4 w-4" />
        </span>
        <div>
          <h3 className="text-sm font-bold text-slate-800">{title}</h3>
          {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
        </div>
      </div>
      <div className="space-y-4 p-5">{children}</div>
    </section>
  )
}

/** Two initials from a name/email, for the client avatar. */
function clientInitials(name: string): string {
  const t = name.trim()
  if (!t) return '?'
  if (t.includes('@') && !t.includes(' ')) return (t.split('@')[0] ?? t).slice(0, 2).toUpperCase()
  return t.split(/\s+/).filter(Boolean).map((p) => p[0]).join('').toUpperCase().slice(0, 2)
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/* ── Attachment preview card (image thumbnail or file icon) ── */
function AttachmentThumb({ file, onRemove }: { file: File; onRemove: () => void }): JSX.Element {
  const isImage = file.type.startsWith('image/') || /\.(jpe?g|png|gif|bmp|webp|svg)$/i.test(file.name)
  const url = useMemo(() => (isImage ? URL.createObjectURL(file) : ''), [file, isImage])


  return (
    <div className="group relative w-[150px] overflow-hidden rounded-[3px] border border-slate-200 bg-white">
      <button
        type="button"
        onClick={onRemove}
        title="Remove"
        className="absolute right-1 top-1 z-10 flex h-5 w-5 items-center justify-center rounded-[3px] bg-black/50 text-white opacity-0 transition-opacity hover:bg-black/70 group-hover:opacity-100"
      >
        <X className="h-3 w-3" />
      </button>
      {isImage && url ? (
        <img
          src={url}
          alt={file.name}
          className="h-[100px] w-full object-cover"
        />
      ) : (
        <div className="flex h-[100px] w-full items-center justify-center bg-[#F5F7FF]">
          <FileText className="h-8 w-8 text-[#3366FF]/70" />
        </div>
      )}
      <div className="px-1.5 py-1">
        <div className="truncate text-[11px] font-medium text-slate-700" title={file.name}>{file.name}</div>
        <div className="text-[10px] text-slate-400">{formatFileSize(file.size)}</div>
      </div>
    </div>
  )
}

/* ── Searchable single-select dropdown ── */
// Estimated menu height (search box + list cap) for the flip-up calculation.
const MENU_EST_HEIGHT = 280
const MENU_GAP = 6

function SelectField({
  icon, value, onChange, disabled, placeholder, error, options, loading,
}: {
  icon?: JSX.Element | undefined
  value: string
  onChange: (v: string) => void
  disabled?: boolean | undefined
  placeholder: string
  error?: string | undefined
  options: { value: string; label: string }[]
  loading?: boolean | undefined
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  // Fixed-position menu anchored to the trigger. `absolute` was clipped by
  // the drawer's overflow-y-auto and each FormSection's overflow-hidden, so
  // a dropdown opened near the section bottom rendered cut off.
  const [menuStyle, setMenuStyle] = useState<{ top: number; left: number; width: number } | null>(null)

  useEffect(() => {
    if (!open) return
    const position = () => {
      const r = ref.current?.getBoundingClientRect()
      if (!r) return
      const flipUp = r.bottom + MENU_GAP + MENU_EST_HEIGHT > window.innerHeight && r.top - MENU_GAP - MENU_EST_HEIGHT > 0
      setMenuStyle({
        top: flipUp ? r.top - MENU_GAP - MENU_EST_HEIGHT : r.bottom + MENU_GAP,
        left: r.left,
        width: r.width,
      })
    }
    position()
    const handler = (e: MouseEvent) => {
      const t = e.target as Node
      if (ref.current?.contains(t) || menuRef.current?.contains(t)) return
      setOpen(false)
    }
    // Re-anchor on scroll/resize — the menu is fixed so it would otherwise
    // detach from the trigger when the drawer content scrolls. Scrolls inside
    // the menu's own option list don't move the trigger, so ignore them.
    const onScrollOrResize = (e: Event) => {
      if (e.target instanceof Node && menuRef.current?.contains(e.target)) return
      position()
    }
    document.addEventListener('mousedown', handler)
    document.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [open])

  const selected = options.find((o) => o.value === value)
  const filtered = query.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`${inputClass} flex items-center justify-between text-left ${icon ? 'pl-9' : ''} ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${error ? 'border-red-300' : 'border-slate-200'}`}
      >
        {icon && <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{icon}</span>}
        <span className={`truncate ${selected ? 'text-slate-800' : 'text-slate-400'}`}>{loading ? 'Loading…' : selected ? selected.label : placeholder}</span>
        {loading ? (
          <span className="ml-2 h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-slate-300 border-t-[#3366FF]" />
        ) : (
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
        )}
      </button>

      {open && !disabled && menuStyle && (
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: menuStyle.top, left: menuStyle.left, width: menuStyle.width, zIndex: 60 }}
          className="max-h-[280px] overflow-hidden rounded-[3px] border border-slate-200 bg-white shadow-xl flex flex-col"
        >
          <div className="relative border-b border-slate-100 p-2">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full rounded-[3px] border border-slate-200 bg-slate-50 py-1.5 pl-9 pr-3 text-sm outline-none focus:border-[#DCE7F8] focus:bg-white focus:ring-2 focus:ring-[#EEF4FF]"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 && <div className="px-3 py-3 text-center text-xs text-slate-400">No options</div>}
            {filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => { onChange(o.value); setOpen(false); setQuery('') }}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
              >
                <span className="truncate">{o.label}</span>
                {value === o.value && <Check className="ml-2 h-4 w-4 shrink-0 text-[#3366FF]" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function Field({ label, required, children, error }: { label: string; required?: boolean | undefined; children: JSX.Element; error?: string | undefined }): JSX.Element {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-semibold text-slate-700">
        {label}{required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  )
}

/* ── Client search dialog (mirrors KC2 "Select Client" button → search) ── */
function ClientDialog({
  open, onClose, onSelect, selectedId,
}: {
  open: boolean
  onClose: () => void
  onSelect: (c: Client) => void
  selectedId: string | undefined
}): JSX.Element | null {
  const [query, setQuery] = useState('')
  const [groupId, setGroupId] = useState('')
  const [groups, setGroups] = useState<{ value: string; label: string }[]>([])
  const [results, setResults] = useState<Client[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Load client groups once when the dialog opens (reference "Select Group" filter).
  useEffect(() => {
    if (!open) return
    let cancelled = false
    api.get<{ clientGroups: { id: string; name: string }[] }>('/admin/client-groups/dropdown')
      .then((res) => { if (!cancelled) setGroups((res.clientGroups ?? []).map((g) => ({ value: g.id, label: g.name }))) })
      .catch(() => { if (!cancelled) setGroups([]) })
    return () => { cancelled = true }
  }, [open])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    const t = setTimeout(() => {
      const q = query.trim()
      // Group selected → list that group's clients; otherwise search all.
      const path = groupId
        ? `/admin/clients/by-group/${groupId}`
        : (q ? `/admin/clients?search=${encodeURIComponent(q)}&limit=30` : '/admin/clients?limit=30')
      api.get<{ clients: Client[] }>(path)
        .then((res) => {
          if (cancelled) return
          let list = res.clients ?? []
          // by-group has no server-side search, so filter locally.
          if (groupId && q) {
            const lc = q.toLowerCase()
            list = list.filter((c) => (c.email ?? '').toLowerCase().includes(lc) || (c.full_name ?? '').toLowerCase().includes(lc))
          }
          setResults(list)
        })
        .catch((e: unknown) => {
          if (!cancelled) { setResults([]); setLoadError(e instanceof Error ? e.message : 'Failed to load clients') }
        })
        .finally(() => { if (!cancelled) setLoading(false) })
    }, 250)
    return () => { cancelled = true; clearTimeout(t) }
  }, [query, groupId, open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-[3px] bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
          <h3 className="text-sm font-bold text-[#0f172a]">Select Client</h3>
          <button onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-[3px] text-slate-400 hover:bg-slate-100 hover:text-slate-600"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-2 border-b border-slate-100 p-3">
          <SelectField icon={<Users className="h-4 w-4" />} value={groupId} onChange={setGroupId} placeholder="All Groups" options={groups} />
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search clients by name or email…"
              className="w-full rounded-[3px] border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-[#DCE7F8] focus:bg-white focus:ring-2 focus:ring-[#EEF4FF]"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {loading && <div className="px-3 py-6 text-center text-xs text-slate-400">Searching…</div>}
          {!loading && loadError && <div className="px-3 py-6 text-center text-xs text-red-500">{loadError}</div>}
          {!loading && !loadError && results.length === 0 && <div className="px-3 py-6 text-center text-xs text-slate-400">No clients found</div>}
          {!loading && results.map((c) => {
            const label = c.full_name || c.email || 'Client'
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => { onSelect(c); onClose() }}
                className={`flex w-full items-center justify-between rounded-[3px] px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50 ${selectedId === c.id ? 'bg-[#F5F7FF]' : ''}`}
              >
                <span className="flex min-w-0 items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[3px] bg-blue-50 text-xs font-semibold text-[#3366FF]">
                    {clientInitials(label)}
                  </span>
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate font-medium text-slate-800">{label}</span>
                    {c.full_name && c.email && <span className="truncate text-xs text-slate-400">{c.email}</span>}
                  </span>
                </span>
                {selectedId === c.id && <Check className="h-4 w-4 shrink-0 text-[#3366FF]" />}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

type GeoRole = 'country' | 'state' | 'city'

function getGeoRole(prop: AdditionalProperty): GeoRole | null {
  const name = prop?.displayName?.toLowerCase() || ''
  if (name.includes('country')) return 'country'
  if (name.includes('state') || name.includes('province')) return 'state'
  if (name.includes('city')) return 'city'
  return null
}

/* ── Dynamic additional-property field ── */
function AdditionalPropertyField({
  prop, value, onChange, disabled, overrideOptions, overrideLoading,
}: {
  prop: AdditionalProperty
  value: unknown
  onChange: (v: unknown) => void
  disabled?: boolean | undefined
  overrideOptions?: PropertyOption[] | undefined
  overrideLoading?: boolean | undefined
}): JSX.Element {
  const strValue = value === undefined || value === null ? '' : String(value)
  const common = { disabled: prop.isReadonly, required: prop.isRequired }

  const constraintProps: Record<string, unknown> = {}
  if (prop.minLength != null) constraintProps.minLength = prop.minLength
  if (prop.maxLength != null) constraintProps.maxLength = prop.maxLength

  switch (prop.inputType) {
    case 'textarea':
    case 'editor':
      return (
        <textarea
          {...common}
          {...constraintProps}
          rows={3}
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={prop.displayName}
          className={`${inputClass} resize-none border-slate-200`}
        />
      )
    case 'number':
    case 'integer':
    case 'decimal':
    case 'rating':
      return (
        <input type="number" {...common} {...constraintProps} value={strValue} onChange={(e) => onChange(e.target.value)} placeholder={prop.displayName} className={`${inputClass} border-slate-200`} />
      )
    case 'date':
      return <DatePicker ariaLabel={prop.displayName} size="sm" disabled={common.disabled} value={strValue} onChange={onChange} />
    case 'datetime':
      return <input type="datetime-local" {...common} value={strValue} onChange={(e) => onChange(e.target.value)} className={`${inputClass} border-slate-200`} />
    case 'checkbox': {
      const checked = strValue === 'true' || strValue === '1'
      return (
        <button
          type="button"
          id={`prop_${prop.columnName}`}
          disabled={prop.isReadonly}
          onClick={() => onChange(checked ? 'false' : 'true')}
          className={`group flex w-full items-start gap-3 rounded-[3px] border px-3.5 py-3 text-left transition-all ${
            checked
              ? 'border-[#3366FF] bg-[#F5F7FF] ring-1 ring-[#3366FF]/20'
              : 'border-slate-200 bg-slate-50/70 hover:border-[#DCE7F8] hover:bg-[#F5F7FF]'
          } ${prop.isReadonly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
        >
          <span
            className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border transition-all ${
              checked
                ? 'border-[#3366FF] bg-[#3366FF] text-white'
                : 'border-slate-300 bg-white group-hover:border-[#3366FF]'
            }`}
          >
            {checked && <Check className="h-3 w-3" strokeWidth={3} />}
          </span>
          <span className="text-sm leading-relaxed text-slate-700">
            {prop.displayName}
            {prop.isRequired && <span className="ml-0.5 text-red-500">*</span>}
          </span>
        </button>
      )
    }
    case 'toggle':
      return (
        <SelectField
          value={strValue}
          onChange={onChange}
          placeholder={`Select ${prop.displayName}`}
          options={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]}
        />
      )
    case 'dropdown':
      return (
        <SelectField
          value={strValue}
          onChange={onChange}
          disabled={prop.isReadonly || disabled}
          placeholder={`Select ${prop.displayName}`}
          options={overrideOptions ?? prop.options}
          loading={overrideLoading}
        />
      )
    default:
      // textbox, email, phone, url, and any unknown type → text input
      return (
        <input type="text" {...common} {...constraintProps} value={strValue} onChange={(e) => onChange(e.target.value)} placeholder={prop.displayName} className={`${inputClass} border-slate-200`} />
      )
  }
}

export default function CreateConversationPanel({ open, onClose, onCreate, parent, onChildCreated }: CreateConversationPanelProps): JSX.Element | null {
  const isChild = !!parent
  const drawerInfo = useDrawerInfo(open)
  const { campaigns } = useCampaigns()
  const { teams, refetch: refetchTeams } = useTeams(true)
  const { users: agentUsers } = useAgentUsers(true)

  const { priorities } = usePriorities(true)
  const { types } = useTypes(true)
  const { categories } = useCategories(true)
  const [categoryId, setCategoryId] = useState('')
  const { subcategories } = useSubcategories(categoryId || undefined)
  const { statuses } = useConversationStatuses()
  const { states } = useStates(true)
  const { impacts } = useImpacts(true)
  const { urgencies } = useUrgencies(true)
  const { complexities } = useComplexities(true)
  const [locationId, setLocationId] = useState('')
  const { locations } = useLocations()
  const [branchId, setBranchId] = useState('')
  const { branches } = useBranches(locationId || undefined)
  const [departmentId, setDepartmentId] = useState('')
  const { departments } = useDepartments(branchId || undefined)
  const { properties: additionalProps } = useAdditionalProperties(true)
  const { forms: conversationForms, loading: conversationFormsLoading } = useConversationForms(true)

  const [conversationFormId, setConversationFormId] = useState('')
  const [clientId, setClientId] = useState<string | undefined>(undefined)
  const [clientName, setClientName] = useState('')
  const [clientEmail, setClientEmail] = useState('')
  const [clientDialogOpen, setClientDialogOpen] = useState(false)
  const [cc, setCc] = useState('')
  const [bcc, setBcc] = useState('')
  // Mail the new ticket to the client + Cc/Bcc (agent-portal tickets have no
  // channel of their own that would tell the client otherwise).
  const [emailClient, setEmailClient] = useState(true)
  const [callUuid, setCallUuid] = useState('')
  const usedCallUUIDs = useRef(new Set<string>())
  const { activeCall } = useCallStore()

  // Auto-populate Call UUID from active call (mirrors KC2 dialer.callID behavior)
  useEffect(() => {
    if (activeCall?.callId && !usedCallUUIDs.current.has(activeCall.callId) && activeCall.status !== 'ended') {
      setCallUuid(activeCall.callId)
    }
  }, [activeCall])
  const [campaignId, setCampaignId] = useState('')
  const [teamId, setTeamId] = useState('')
  const [queueId, setQueueId] = useState('')
  const [priorityId, setPriorityId] = useState('')
  const [typeId, setTypeId] = useState('')
  const [subcategoryId, setSubcategoryId] = useState('')
  const [statusId, setStatusId] = useState('')
  const [stateId, setStateId] = useState('')
  const [impactId, setImpactId] = useState('')
  const [urgencyId, setUrgencyId] = useState('')
  const [complexityId, setComplexityId] = useState('')
  // Agent is stored as the real agents.id (see useAgentUsers) so it can be
  // written straight to conversations.assigned_agent_id with no server lookup.
  const [agentId, setAgentId] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [tagInput, setTagInput] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [propValues, setPropValues] = useState<Record<string, unknown>>({})
  const [attachments, setAttachments] = useState<File[]>([])
  const [attachmentIds, setAttachmentIds] = useState<string[]>([])
  const [uploadingAttachments, setUploadingAttachments] = useState(false)
  const { upload } = useUploadAttachment()
  const [formDetail, setFormDetail] = useState<FormDetail | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [copy, setCopy] = useState<CopyFromParentOptions>(DEFAULT_COPY_OPTIONS)
  const boxRef = useRef<HTMLDivElement>(null)

  // Geo cascade: cached options for state/city fetched on-demand by parent value.
  const [cascadeOptions, setCascadeOptions] = useState<Record<string, PropertyOption[]>>({})
  const [cascadeLoading, setCascadeLoading] = useState<Record<string, boolean>>({})

  const fetchCascadeOptions = useCallback(async (propId: number, parentValue: string): Promise<void> => {
    const key = `${propId}_${parentValue}`
    if (cascadeOptions[key] || cascadeLoading[key]) return
    setCascadeLoading((prev) => ({ ...prev, [key]: true }))
    try {
      const res = await api.get<{ options: { id?: number | string; label?: string; value?: string; name?: string }[] }>(
        `/admin/conversation-management/additional-properties/${propId}/dropdown-options?parentOption=${encodeURIComponent(parentValue)}`,
      )
      const opts = (res.options ?? [])
        .map((o) => ({ value: String(o.value ?? o.id ?? ''), label: String(o.label ?? o.name ?? o.value ?? o.id ?? '') }))
        .filter((o) => o.value !== '' && o.label !== '')
      setCascadeOptions((prev) => ({ ...prev, [key]: opts }))
    } catch {
      // silently ignore
    } finally {
      setCascadeLoading((prev) => ({ ...prev, [key]: false }))
    }
  }, [cascadeOptions, cascadeLoading])

  useEffect(() => { if (open) void refetchTeams() }, [open, refetchTeams])

  // Child mode: start each child-ticket form fresh whenever a different
  // parent's form is (re)opened, so stale copies never carry over.
  useEffect(() => {
    if (!open || !parent) return
    setCopy(DEFAULT_COPY_OPTIONS)
    setSubject(''); setMessage('')
    setClientId(undefined); setClientName(''); setClientEmail('')
    setCampaignId(''); setTeamId(''); setPriorityId(''); setTags([])
    setErrors({}); setSubmitError(null)
  }, [open, parent?.conversationId])

  // Cascade: teams filtered by the selected campaign (dropdown returns campaignId).
  const teamOptions = useMemo(
    () => teams
      .filter((t) => !campaignId || String(t.campaignId ?? '') === campaignId)
      .map((t) => ({ value: t.id, label: t.name })),
    [teams, campaignId],
  )
  const campaignOptions = useMemo(() => campaigns.map((c) => ({ value: c.id, label: c.name })), [campaigns])
  // Agents = users who actually have an agent record, filtered by the selected
  // campaign and (when one is chosen) team. A user matches the campaign if
  // their primary campaignId OR any of their campaignIds (from campaign_users)
  // equals it, and matches the team the same way via teamId/teamIds. With no
  // team selected, all agents in the campaign stay selectable.
  const agentOptions = useMemo(
    () => !campaignId ? [] : agentUsers
      .filter((u) => u.agentId
        && (u.campaignIds?.includes(campaignId) || String(u.campaignId ?? '') === campaignId)
        && (!teamId || u.teamIds?.includes(teamId) || String(u.teamId ?? '') === teamId))
      .map((u) => ({ value: u.agentId as string, label: u.loginid || u.name || 'Agent' })),
    [agentUsers, campaignId, teamId],
  )
  const formOptions = useMemo(() => conversationForms.map((f) => ({ value: f.id, label: f.name })), [conversationForms])
  // The conversation form is mandatory — it decides which additional
  // properties the conversation carries and gates the subject/description
  const formRequired = true
  const priorityOptions = useMemo(() => priorities.map((p) => ({ value: p.id, label: p.name })), [priorities])
  const typeOptions = useMemo(() => types.map((t) => ({ value: t.id, label: t.name })), [types])
  const categoryOptions = useMemo(() => categories.map((c) => ({ value: c.id, label: c.name })), [categories])
  const subcategoryOptions = useMemo(() => subcategories.map((s) => ({ value: s.id, label: s.name })), [subcategories])
  const statusOptions = useMemo(() => statuses.map((s) => ({ value: s.id, label: s.name })), [statuses])
  const stateOptions = useMemo(() => states.map((s) => ({ value: s.id, label: s.name })), [states])
  const impactOptions = useMemo(() => impacts.map((i) => ({ value: i.id, label: i.name })), [impacts])
  const urgencyOptions = useMemo(() => urgencies.map((u) => ({ value: u.id, label: u.name })), [urgencies])
  const complexityOptions = useMemo(() => complexities.map((c) => ({ value: c.id, label: c.name })), [complexities])
  const locationOptions = useMemo(() => locations.map((l) => ({ value: l.id, label: l.name })), [locations])
  const branchOptions = useMemo(() => branches.map((b) => ({ value: b.id, label: b.name })), [branches])
  const departmentOptions = useMemo(() => departments.map((d) => ({ value: d.id, label: d.name })), [departments])

  // Auto-select the tenant's default conversation form when the panel opens
  // (and no form has been chosen yet), so the agent doesn't have to pick it
  // manually every time. Mirrors the KC2 reference where the default form is
  // pre-selected on a new interaction.
  useEffect(() => {
    if (!open || conversationFormId || conversationFormsLoading) return
    const defaultForm = conversationForms.find((f) => f.isDefault)
    if (defaultForm) setConversationFormId(defaultForm.id)
  }, [open, conversationFormId, conversationForms, conversationFormsLoading])

  // The selected conversation form drives which extra fields appear (mirrors the
  // reference: picking an Interaction Form loads its properties). No form → no
  // additional-property fields.
  useEffect(() => {
    if (!conversationFormId) { setFormDetail(null); return }
    let cancelled = false
    api.get<{ form: FormDetail }>(`/admin/conversation-management/forms/${conversationFormId}`)
      .then((res) => { if (!cancelled) setFormDetail(res.form) })
      .catch(() => { if (!cancelled) setFormDetail(null) })
    return () => { cancelled = true }
  }, [conversationFormId])

  const visibleProps = useMemo(() => {
    if (!formDetail) return []
    const selected = new Set((formDetail.selectedProperties ?? []).map(String))
    return additionalProps.filter((p) => selected.has(String(p.id)))
  }, [formDetail, additionalProps])

  // Find geo-cascade properties among the visible set.
  const geoProps = useMemo(() => {
    const country = visibleProps.find((p) => getGeoRole(p) === 'country')
    const state = visibleProps.find((p) => getGeoRole(p) === 'state')
    const city = visibleProps.find((p) => getGeoRole(p) === 'city')
    return { country, state, city }
  }, [visibleProps])

  // Apply property defaults from the form definition when the form is loaded.
  useEffect(() => {
    if (!formDetail?.propertyDefaults || !visibleProps.length) return
    const defaults = formDetail.propertyDefaults
    setPropValues((prev) => {
      const next = { ...prev }
      let changed = false
      for (const prop of visibleProps) {
        const propId = String(prop.id)
        const defaultVal = defaults[propId]
        if (defaultVal !== undefined && defaultVal !== '') {
          const currentVal = next[prop.columnName]
          if (currentVal === undefined || currentVal === null || currentVal === '') {
            next[prop.columnName] = defaultVal
            changed = true
          }
        }
      }
      return changed ? next : prev
    })
  }, [formDetail, visibleProps])

  // Auto-fetch cascade options when parent values are present.
  useEffect(() => {
    if (geoProps.state && geoProps.country) {
      const countryVal = String(propValues[geoProps.country.columnName] ?? '')
      if (countryVal) void fetchCascadeOptions(geoProps.state.id, countryVal)
    }
    if (geoProps.city && geoProps.state) {
      const stateVal = String(propValues[geoProps.state.columnName] ?? '')
      if (stateVal) void fetchCascadeOptions(geoProps.city.id, stateVal)
    }
  }, [geoProps, propValues, fetchCascadeOptions])

  // Compute geo cascade options and disabled state for a property.
  const getGeoCascadeProps = (prop: AdditionalProperty): {
    overrideOptions?: PropertyOption[] | undefined
    overrideLoading?: boolean | undefined
    disabled?: boolean | undefined
  } => {
    const role = getGeoRole(prop)
    if (!role || role === 'country') return {}
    const parentRole: GeoRole = role === 'state' ? 'country' : 'state'
    const parentProp = parentRole === 'country' ? geoProps.country : geoProps.state
    if (!parentProp) return {}
    const parentVal = String(propValues[parentProp.columnName] ?? '')
    if (!parentVal) return { disabled: true }
    const key = `${prop.id}_${parentVal}`
    return {
      overrideOptions: cascadeOptions[key],
      overrideLoading: cascadeLoading[key],
    }
  }

  // Ordered stream of conversation fields (additional properties and
  // separators) following the form's formPropertiesOrder. Subject and
  // description are rendered in the Customer section instead. Campaign,
  // team, agent, and priority are always rendered first because they drive routing.
  const orderedItems = useMemo<OrderedItem[]>(() => {
    if (!formDetail) return []
    let separators: Separator[] = []
    try {
      const parsed = JSON.parse(formDetail.formSeparators || '[]')
      if (Array.isArray(parsed)) separators = parsed as Separator[]
    } catch { separators = [] }

    const propById = new Map(visibleProps.map((p) => [String(p.id), p]))
    const separatorById = new Map(separators.map((s) => [`separator_${s.id}`, s]))
    const order = formDetail.formPropertiesOrder ?? []

    // Desired keys that belong in the right-hand Conversation stream. Subject and
    // description are rendered in the left-hand Customer section (below Cc/Bcc,
    // above Attachments) once a form is selected, so they are excluded here.
    const FIXED_FIELD_KEYS = new Set([
      'priority_id',
      'type_id',
      'category_id',
      'subcategory_id',
      'status_id',
      'state_id',
      'impact_id',
      'urgency_id',
      'complexity_id',
      'location_id',
      'branch_id',
      'department_id',
    ])

    const interactionProps = new Set((formDetail.interactionProperties ?? []).map(String))
    const allowedFixed = new Set([...FIXED_FIELD_KEYS].filter((k) => interactionProps.has(k)))
    const allowedAdditional = new Set(visibleProps.map((p) => String(p.id)))

    // Desired keys that should appear in the Conversation section
    const desired = new Set<string>([...allowedFixed, ...allowedAdditional])

    // Reconcile the saved order against the desired set (and any explicit
    // separator entries), then append desired keys missing from the saved order.
    const orderedKeys = [
      ...order.filter((k) => desired.has(k) || (k.startsWith('separator_') && separatorById.has(k))),
      ...[...desired].filter((k) => !order.includes(k)),
    ]

    const items: OrderedItem[] = []
    for (const key of orderedKeys) {
      if (key === 'subject') { items.push({ type: 'core', key: 'subject' }); continue }
      if (key === 'description') { items.push({ type: 'core', key: 'description' }); continue }
      if (key.startsWith('separator_')) {
        const sep = separatorById.get(key)
        if (sep) items.push({ type: 'separator', label: sep.label })
        continue
      }
      if (allowedFixed.has(key)) {
        items.push({ type: 'fixed', key, label: FIXED_FIELD_LABELS[key] || key })
        continue
      }
      const prop = propById.get(key)
      if (prop) items.push({ type: 'prop', prop })
    }
    return items
  }, [formDetail, visibleProps])

  const renderFixedField = (key: string) => {
    switch (key) {
      case 'priority_id':
        return (
          <Field key="priority_id" label="Priority">
            <SelectField
              icon={<Flag className="h-4 w-4" />}
              value={priorityId}
              onChange={setPriorityId}
              placeholder="Select Priority"
              options={priorityOptions}
            />
          </Field>
        )
      case 'type_id':
        return (
          <Field key="type_id" label="Type">
            <SelectField
              icon={<Layers className="h-4 w-4" />}
              value={typeId}
              onChange={setTypeId}
              placeholder="Select Type"
              options={typeOptions}
            />
          </Field>
        )
      case 'category_id':
        return (
          <Field key="category_id" label="Category">
            <SelectField
              icon={<Tag className="h-4 w-4" />}
              value={categoryId}
              onChange={(v) => {
                setCategoryId(v)
                setSubcategoryId('')
              }}
              placeholder="Select Category"
              options={categoryOptions}
            />
          </Field>
        )
      case 'subcategory_id':
        return (
          <Field key="subcategory_id" label="Subcategory">
            <SelectField
              icon={<Tag className="h-4 w-4" />}
              value={subcategoryId}
              onChange={setSubcategoryId}
              disabled={!categoryId}
              placeholder={categoryId ? 'Select Subcategory' : 'Select a Category first'}
              options={subcategoryOptions}
            />
          </Field>
        )
      case 'status_id':
        return (
          <Field key="status_id" label="Status">
            <SelectField
              icon={<SlidersHorizontal className="h-4 w-4" />}
              value={statusId}
              onChange={setStatusId}
              placeholder="Select Status"
              options={statusOptions}
            />
          </Field>
        )
      case 'state_id':
        return (
          <Field key="state_id" label="State">
            <SelectField
              icon={<SlidersHorizontal className="h-4 w-4" />}
              value={stateId}
              onChange={setStateId}
              placeholder="Select State"
              options={stateOptions}
            />
          </Field>
        )
      case 'impact_id':
        return (
          <Field key="impact_id" label="Impact">
            <SelectField
              icon={<SlidersHorizontal className="h-4 w-4" />}
              value={impactId}
              onChange={setImpactId}
              placeholder="Select Impact"
              options={impactOptions}
            />
          </Field>
        )
      case 'urgency_id':
        return (
          <Field key="urgency_id" label="Urgency">
            <SelectField
              icon={<SlidersHorizontal className="h-4 w-4" />}
              value={urgencyId}
              onChange={setUrgencyId}
              placeholder="Select Urgency"
              options={urgencyOptions}
            />
          </Field>
        )
      case 'complexity_id':
        return (
          <Field key="complexity_id" label="Complexity">
            <SelectField
              icon={<SlidersHorizontal className="h-4 w-4" />}
              value={complexityId}
              onChange={setComplexityId}
              placeholder="Select Complexity"
              options={complexityOptions}
            />
          </Field>
        )
      case 'location_id':
        return (
          <Field key="location_id" label="Location">
            <SelectField
              icon={<Building2 className="h-4 w-4" />}
              value={locationId}
              onChange={(v) => {
                setLocationId(v)
                setBranchId('')
                setDepartmentId('')
              }}
              placeholder="Select Location"
              options={locationOptions}
            />
          </Field>
        )
      case 'branch_id':
        return (
          <Field key="branch_id" label="Branch">
            <SelectField
              icon={<Building2 className="h-4 w-4" />}
              value={branchId}
              onChange={(v) => {
                setBranchId(v)
                setDepartmentId('')
              }}
              disabled={!locationId}
              placeholder={locationId ? 'Select Branch' : 'Select a Location first'}
              options={branchOptions}
            />
          </Field>
        )
      case 'department_id':
        return (
          <Field key="department_id" label="Department">
            <SelectField
              icon={<Building2 className="h-4 w-4" />}
              value={departmentId}
              onChange={setDepartmentId}
              disabled={!branchId}
              placeholder={branchId ? 'Select Department' : 'Select a Branch first'}
              options={departmentOptions}
            />
          </Field>
        )
      default:
        return null
    }
  }

  if (!open) return null

  const validate = (): boolean => {
    const errs: Record<string, string> = {}
    if (formRequired && !conversationFormId) errs.conversationFormId = 'Conversation form is required'
    if (!clientId) errs.clientId = 'Client is required'
    if (!campaignId) errs.campaignId = 'Campaign is required'
    if (!teamId) errs.teamId = 'Team is required'
    if (!subject.trim()) errs.subject = 'Subject is required'
    if (splitAddresses(cc).some((a) => !isValidEmail(a))) errs.cc = 'Enter valid email addresses separated by commas'
    if (splitAddresses(bcc).some((a) => !isValidEmail(a))) errs.bcc = 'Enter valid email addresses separated by commas'
    // message is HTML from the rich text editor — strip tags before length-checking.
    const plainText = message.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
    if (isEmptyHtml(message) || plainText.length < 10) errs.message = 'Description must be at least 10 characters'
    for (const p of visibleProps) {
      if (p.isReadonly) continue
      const v = propValues[p.columnName]
      const strV = v === undefined || v === null ? '' : String(v)
      if (p.isRequired && strV.trim() === '') {
        errs[`prop_${p.columnName}`] = `${p.displayName} is required`
        continue
      }
      if (strV.trim() === '') continue
      const len = strV.trim().length
      if (p.minLength != null && len < p.minLength) errs[`prop_${p.columnName}`] = `${p.displayName} must be at least ${p.minLength} characters`
      if (p.maxLength != null && len > p.maxLength) errs[`prop_${p.columnName}`] = `${p.displayName} must be at most ${p.maxLength} characters`
    }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const reset = () => {
    setConversationFormId('')
    setClientId(undefined); setClientName(''); setClientEmail(''); setCc(''); setBcc(''); setCallUuid(''); setEmailClient(true)
    setCampaignId(''); setTeamId(''); setQueueId(''); setAgentId(''); setPriorityId('')
    setTypeId(''); setCategoryId(''); setSubcategoryId(''); setStatusId(''); setStateId('')
    setImpactId(''); setUrgencyId(''); setComplexityId(''); setLocationId(''); setBranchId(''); setDepartmentId('')
    setSubject(''); setMessage('')
    setTagInput(''); setTags([]); setPropValues({}); setErrors({}); setFormDetail(null); setAttachments([]); setAttachmentIds([])
    setSubmitError(null)
    setCopy(DEFAULT_COPY_OPTIONS)
  }

  // Toggle a "Copy from Parent" option. Field copies pre-fill the form
  // immediately; attachment/checklist copies are flags the API acts on.
  const handleCopyToggle = (key: keyof CopyFromParentOptions, checked: boolean): void => {
    setCopy((prev) => ({ ...prev, [key]: checked }))
    if (!parent) return
    switch (key) {
      case 'copySubject':
        setSubject(checked ? (parent.subject ?? '') : '')
        break
      case 'copyDescription':
        setMessage(checked ? (parent.description ?? '') : '')
        break
      case 'copyClient':
        if (checked) {
          setClientId(parent.clientId ?? undefined)
          setClientName(parent.clientName ?? '')
          setClientEmail(parent.clientEmail ?? '')
        } else {
          setClientId(undefined); setClientName(''); setClientEmail('')
        }
        break
      case 'copyCampaignTeam':
        if (checked) {
          setCampaignId(parent.campaignId ?? '')
          setTeamId(parent.teamId ?? '')
        } else {
          setCampaignId(''); setTeamId('')
        }
        break
      case 'copyPriority':
        setPriorityId(checked ? (parent.priorityId ?? '') : '')
        break
      case 'copyTags':
        setTags(checked ? (parent.tags ?? []) : [])
        break
      default:
        // copyAttachments / copyChecklists — flags only (API performs the copy)
        break
    }
  }

  // Which copy options are offered (a field the parent doesn't have is disabled).
  const copyItems: { key: keyof CopyFromParentOptions; label: string; disabled: boolean; icon: LucideIcon }[] = parent
    ? [
        { key: 'copySubject', label: 'Subject', disabled: !parent.subject, icon: Type },
        { key: 'copyDescription', label: 'Description', disabled: !parent.description, icon: AlignLeft },
        { key: 'copyClient', label: 'Client', disabled: !parent.clientId, icon: User },
        { key: 'copyCampaignTeam', label: 'Campaign & Team', disabled: !parent.campaignId, icon: Users },
        { key: 'copyPriority', label: 'Priority', disabled: !parent.priorityId, icon: Flag },
        { key: 'copyTags', label: 'Tags', disabled: !(parent.tags && parent.tags.length), icon: Tag },
        { key: 'copyAttachments', label: 'Attachments', disabled: false, icon: Paperclip },
        { key: 'copyChecklists', label: 'Checklist', disabled: false, icon: ListChecks },
        { key: 'copyAdditionalProperties', label: 'Additional Properties', disabled: false, icon: SlidersHorizontal },
        { key: 'copyGlobalNotes', label: 'Global Notes', disabled: false, icon: StickyNote },
        { key: 'copyTimeline', label: 'Timeline', disabled: false, icon: Clock },
      ]
    : []

  const addFiles = (files: FileList | null): void => {
    if (!files || files.length === 0) return
    const list = Array.from(files)
    setAttachments((prev) => [...prev, ...list])
    setUploadingAttachments(true)
    let remaining = list.length
    for (const file of list) {
      void (async () => {
        try {
          const res = await upload(file)
          setAttachmentIds((prev) => [...prev, res.attachmentId])
        } catch (err) {
          console.error('[CreateConversationPanel] Upload failed', { name: file.name, error: (err as Error)?.message })
        } finally {
          remaining -= 1
          if (remaining === 0) setUploadingAttachments(false)
        }
      })()
    }
  }

  const handleSubmit = async () => {
    if (!validate() || submitting || uploadingAttachments) return
    setSubmitting(true)
    setSubmitError(null)
    const agentLabel = agentUsers.find((u) => u.agentId === agentId)?.loginid ?? undefined
    const data: NewConversationData = {
      channel: FIXED_CHANNEL,
      source: FIXED_SOURCE,
      conversationFormId: conversationFormId || undefined,
      clientId,
      customerName: clientName,
      cc: cc.trim(),
      bcc: bcc.trim(),
      callUuid: callUuid.trim(),
      campaignId,
      teamId,
      queueId: queueId || undefined,
      agentId: agentId || undefined,
      subject: subject.trim(),
      message,
      priorityId: priorityId || undefined,
      typeId: typeId || undefined,
      categoryId: categoryId || undefined,
      subcategoryId: subcategoryId || undefined,
      statusId: statusId || undefined,
      stateId: stateId || undefined,
      impactId: impactId || undefined,
      urgencyId: urgencyId || undefined,
      complexityId: complexityId || undefined,
      locationId: locationId || undefined,
      branchId: branchId || undefined,
      departmentId: departmentId || undefined,
      tags,
      assignedTo: agentLabel,
      additionalProperties: propValues,
      attachments,
    }
    try {
      const res = await api.post<{ conversationId: string }>('/conversations', {
        channel: FIXED_CHANNEL,
        senderHandle: clientEmail || clientName || 'web-client',
        ...(clientEmail && clientName ? { senderName: clientName } : {}),
        subject: subject.trim(),
        description: message,
        ...(agentId ? { assignedAgentId: agentId } : {}),
        ...(teamId ? { assignedTeamId: teamId } : {}),
        ...(campaignId ? { campaignId } : {}),
        ...(queueId ? { assignedQueueId: queueId } : {}),
        ...(priorityId ? { priorityId } : {}),
        ...(typeId ? { typeId } : {}),
        ...(categoryId ? { categoryId } : {}),
        ...(subcategoryId ? { subcategoryId } : {}),
        ...(statusId ? { statusId } : {}),
        ...(stateId ? { stateId } : {}),
        ...(impactId ? { impactId } : {}),
        ...(urgencyId ? { urgencyId } : {}),
        ...(complexityId ? { complexityId } : {}),
        ...(locationId ? { locationId } : {}),
        ...(branchId ? { branchId } : {}),
        ...(departmentId ? { departmentId } : {}),
        ...(attachmentIds.length > 0 ? { attachmentIds } : {}),
        // The server falls back to the client record's address when `to` is empty.
        notifyEmail: emailClient
          ? {
              to: isValidEmail(clientEmail) ? [clientEmail.trim()] : [],
              cc: splitAddresses(cc),
              bcc: splitAddresses(bcc),
            }
          : false,
        // Child mode: link to the parent and request the related-entity copies.
        ...(parent
          ? {
              parentId: parent.conversationId,
              isParent: false,
              copyFromParent: {
                copyAttachments: copy.copyAttachments,
                copyChecklists: copy.copyChecklists,
                copyAdditionalProperties: copy.copyAdditionalProperties,
                copyGlobalNotes: copy.copyGlobalNotes,
                copyTimeline: copy.copyTimeline,
              },
            }
          : {}),
        metadata: {
          source: FIXED_SOURCE,
          clientId,
          conversationFormId: conversationFormId || undefined,
          campaignId,
          teamId,
          queueId: queueId || undefined,
          agentId: agentId || undefined,
          priorityId: priorityId || undefined,
          typeId: typeId || undefined,
          categoryId: categoryId || undefined,
          subcategoryId: subcategoryId || undefined,
          statusId: statusId || undefined,
          stateId: stateId || undefined,
          impactId: impactId || undefined,
          urgencyId: urgencyId || undefined,
          complexityId: complexityId || undefined,
          locationId: locationId || undefined,
          branchId: branchId || undefined,
          departmentId: departmentId || undefined,
          cc: cc.trim(),
          bcc: bcc.trim(),
          ...(callUuid.trim() ? { callUuid: callUuid.trim() } : {}),
          tags,
          additionalProperties: propValues,
        },
      })
      if (parent && onChildCreated) onChildCreated(res.conversationId)
      else onCreate(data)
      if (callUuid) usedCallUUIDs.current.add(callUuid)
      reset()
      onClose()
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Failed to create conversation')
    } finally {
      setSubmitting(false)
    }
  }

  const handleCampaignChange = (v: string) => {
    setCampaignId(v)
    setTeamId((prev) => {
      const stillValid = teams.some((t) => t.id === prev && String(t.campaignId ?? '') === v)
      return stillValid ? prev : ''
    })
  }

  // Team change: drop the picked agent if they aren't in the newly selected
  // team — the agent list is team-scoped, so a stale pick would no longer be
  // selectable.
  const handleTeamChange = (v: string) => {
    setTeamId(v)
    setAgentId((prev) => {
      if (!prev || !v) return prev
      const agent = agentUsers.find((u) => u.agentId === prev)
      const inTeam = agent && (agent.teamIds?.includes(v) || String(agent.teamId ?? '') === v)
      return inTeam ? prev : ''
    })
  }

  const addTag = () => {
    const t = tagInput.trim()
    if (t && !tags.includes(t)) setTags((prev) => [...prev, t])
    setTagInput('')
  }
  const removeTag = (t: string) => setTags((prev) => prev.filter((x) => x !== t))

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm transition-opacity" onClick={onClose} />

      {/* Drawer */}
      <div
        ref={boxRef}
        className="animate-slide-in-right fixed right-0 top-0 z-50 flex h-full w-[88%] max-w-[1280px] flex-col bg-[#F8FAFC] shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-8 py-4">
          <div className="flex items-center gap-3.5">
            <div className="flex h-11 w-11 items-center justify-center rounded-[3px] bg-[#3366FF] text-white shadow-sm">
              {isChild ? <Network className="h-[22px] w-[22px]" /> : <MessageSquarePlus className="h-[22px] w-[22px]" />}
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#0f172a]">{isChild ? 'New Child Conversation' : 'New Conversation'}</h2>
              <p className="text-xs text-slate-400">
                {isChild ? 'Create a child conversation linked to the parent' : 'Create and route a new customer interaction'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isChild ? (
              <div className="hidden items-center gap-2 rounded-[3px] border border-[#DCE7F8] bg-[#F5F7FF] px-3 py-1.5 sm:flex">
                <Network className="h-3.5 w-3.5 text-[#3366FF]" />
                <span className="text-[11px] text-slate-400">Parent</span>
                <span className="text-xs font-semibold text-slate-700">#{parent?.referenceId ?? '—'}</span>
              </div>
            ) : (
              <div className="hidden items-center gap-2 rounded-[3px] border border-[#DCE7F8] bg-[#F5F7FF] px-3 py-1.5 sm:flex">
                <Radio className="h-3.5 w-3.5 text-[#3366FF]" />
                <span className="text-[11px] text-slate-400">Source</span>
                <span className="text-xs font-semibold text-slate-700">{SOURCE_LABEL}</span>
              </div>
            )}
            <DrawerInfoButton shown={drawerInfo.shown} onToggle={drawerInfo.toggle} />
            <button
              onClick={onClose}
              aria-label="Close"
              className="flex h-9 w-9 items-center justify-center rounded-[3px] text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <DrawerInfoPanel
          info={
            isChild
              ? {
                  lead: `Raises a new conversation that hangs off ${parent?.referenceId ? `#${parent.referenceId}` : 'the parent'} — use it to split a distinct piece of work out of one request.`,
                  points: [
                    { term: 'Copy from Parent', detail: 'Ticks decide which of the parent\u2019s details are pre-filled. Everything stays editable afterwards.' },
                    { term: 'Independent', detail: 'The child gets its own reference, assignment and SLA clock — it is not a copy of the parent.' },
                    { term: 'Closing', detail: 'The parent cannot be closed while this child is still open.' },
                    { term: 'Merging', detail: 'Conversations in a parent/child relationship cannot be merged — detach them first.' },
                  ],
                }
              : {
                  lead: 'Raises a conversation on the customer\u2019s behalf, as if it had arrived on the channel you pick.',
                  points: [
                    { term: 'Client', detail: 'Who the conversation is for. Picking an existing client links their history; a new one is created if there is no match.' },
                    { term: 'Channel', detail: 'How replies will leave the platform — it must be a channel that customer can actually receive.' },
                    { term: 'Routing', detail: 'Campaign, team and agent decide whose queue it lands in. Leave the agent blank to let the team pick it up.' },
                    { term: 'Classification', detail: 'Type, category and priority drive the SLA policy applied on save.' },
                  ],
                }
          }
          shown={drawerInfo.shown}
          onDismiss={drawerInfo.toggle}
        />

        {/* Scrollable form — sectioned layout */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          {/* Child mode: Copy from Parent */}
          {isChild && (
            <div className="mb-5 overflow-hidden rounded-[3px] border border-[#DCE7F8] bg-[#F5F7FF] shadow-sm">
              <div className="flex items-center gap-3 border-b border-[#DCE7F8] px-5 py-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-[3px] bg-[#3366FF]/10 text-[#3366FF]">
                  <Copy className="h-4 w-4" />
                </span>
                <div>
                  <h3 className="text-sm font-bold text-slate-800">Copy from Parent</h3>
                  <p className="text-[11px] text-slate-400">
                    Reuse details from parent {parent?.referenceId ? `#${parent.referenceId}` : 'ticket'} for this child
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2.5 p-5 sm:grid-cols-4">
                {copyItems.map((it) => {
                  const checked = copy[it.key]
                  const Icon = it.icon
                  return (
                    <button
                      key={it.key}
                      type="button"
                      disabled={it.disabled}
                      onClick={() => handleCopyToggle(it.key, !checked)}
                      title={it.disabled ? 'Parent has no value for this field' : undefined}
                      className={`group relative flex items-center gap-2.5 rounded-[3px] border px-3 py-2.5 text-left transition-all ${
                        it.disabled
                          ? 'cursor-not-allowed border-slate-200 bg-slate-50 opacity-50'
                          : checked
                            ? 'border-[#3366FF] bg-white shadow-sm ring-1 ring-[#3366FF]'
                            : 'border-slate-200 bg-white hover:border-[#DCE7F8] hover:bg-[#F5F7FF]'
                      }`}
                    >
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[3px] transition-colors ${
                          checked ? 'bg-[#3366FF] text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-blue-50 group-hover:text-[#3366FF]'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className={`min-w-0 flex-1 truncate text-sm font-medium ${checked ? 'text-[#0f172a]' : 'text-slate-600'}`}>
                        {it.label}
                      </span>
                      <span
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors ${
                          checked ? 'border-[#3366FF] bg-[#3366FF] text-white' : 'border-slate-300 bg-white'
                        }`}
                      >
                        {checked && <Check className="h-3 w-3" />}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
            {/* ── LEFT: Customer ── */}
            <FormSection icon={User} title="Customer" hint="Who this conversation is with">
              <Field label="Conversation Form" required={formRequired} error={errors.conversationFormId}>
                <SelectField icon={<FileText className="h-4 w-4" />} value={conversationFormId} onChange={setConversationFormId} placeholder="Select a form" options={formOptions} loading={conversationFormsLoading} error={errors.conversationFormId} />
              </Field>

              {callUuid && (
                <Field label="Call UUID">
                  <div className="flex items-center gap-2 rounded-[3px] border border-[#DCE7F8] bg-[#F5F7FF] px-3 py-2">
                    <Phone className="h-4 w-4 shrink-0 text-[#3366FF]" />
                    <span className="truncate font-mono text-xs font-semibold text-[#3366FF]">{callUuid}</span>
                    {activeCall?.callerCLI && (
                      <span className="ml-1 text-xs text-slate-400">({activeCall.callerCLI})</span>
                    )}
                    <button
                      type="button"
                      title="Remove Call UUID"
                      onClick={() => {
                        usedCallUUIDs.current.add(callUuid)
                        setCallUuid('')
                      }}
                      className="ml-auto flex h-5 w-5 shrink-0 items-center justify-center rounded-[3px] text-red-500 transition-colors hover:bg-red-50 hover:text-red-700"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </Field>
              )}

              <Field label="Client" required error={errors.clientId}>
                <button
                  type="button"
                  onClick={() => setClientDialogOpen(true)}
                  className={`flex w-full items-center justify-between rounded-[3px] border bg-slate-50/70 px-3 py-2 text-left transition-all hover:bg-slate-50 ${errors.clientId ? 'border-red-300' : 'border-slate-200'}`}
                >
                  {clientId ? (
                    <span className="flex min-w-0 items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[3px] bg-blue-50 text-xs font-semibold text-[#3366FF]">
                        {clientInitials(clientName)}
                      </span>
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-medium text-slate-800">{clientName}</span>
                        {clientEmail && clientEmail !== clientName && (
                          <span className="truncate text-xs text-slate-400">{clientEmail}</span>
                        )}
                      </span>
                    </span>
                  ) : (
                    <span className="flex items-center gap-2 py-1 text-sm text-slate-400">
                      <User className="h-4 w-4" /> Select a client
                    </span>
                  )}
                  <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-[#3366FF]">
                    <Search className="h-4 w-4" />
                    {clientId ? 'Change' : 'Browse'}
                  </span>
                </button>
              </Field>

              <Field label="Cc" error={errors.cc}>
                <input type="text" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="abc@gmail.com, xyz@outlook.com" className={`${inputClass} ${errors.cc ? 'border-red-300' : 'border-slate-200'}`} />
              </Field>
              <Field label="Bcc" error={errors.bcc}>
                <input type="text" value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="abc@gmail.com, xyz@outlook.com" className={`${inputClass} ${errors.bcc ? 'border-red-300' : 'border-slate-200'}`} />
              </Field>
              <label className="flex items-start gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={emailClient}
                  onChange={(e) => setEmailClient(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 accent-[#3366FF]"
                />
                <span>
                  Email this ticket to the client{cc.trim() || bcc.trim() ? ' and Cc/Bcc' : ''}
                  {emailClient && clientId && !clientEmail.trim() && !splitAddresses(cc).length && (
                    <span className="block text-[11px] text-slate-500">The client email will be resolved automatically from the client record.</span>
                  )}
                </span>
              </label>

              {/* Subject & Description appear here once a conversation form is
                  selected — they are gated by the form's showSubject/showDescription
                  flags (both default to true). They render in the Customer section
                  (below Cc/Bcc, above Attachments) instead of the Conversation section. */}
              {formDetail?.showSubject && (
                <Field label="Subject" required error={errors.subject}>
                  <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Enter a subject line" className={`${inputClass} ${errors.subject ? 'border-red-300' : 'border-slate-200'}`} />
                </Field>
              )}
              {formDetail?.showDescription && (
                <Field label="Description" required error={errors.message}>
                  <RichTextEditor value={message} onChange={setMessage} height={220} placeholder="Describe the interaction (minimum 10 characters)…" />
                </Field>
              )}

              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Initial Attachments</label>
                <label className="flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-[3px] border border-dashed border-slate-300 bg-slate-50/70 px-4 py-6 text-center transition-colors hover:border-[#3366FF] hover:bg-[#F5F7FF]">
                  <Paperclip className="h-5 w-5 text-slate-400" />
                  <span className="text-xs font-medium text-slate-600">Drag &amp; drop files here, or click to select</span>
                  <span className="text-[10px] leading-relaxed text-slate-400">
                    Only .doc, .docx, .xls, .xlsx, .ppt, .pptx, .jpg, .jpeg, .gif, .png, .pdf, .txt, .odt, .ods, .zip, .rar, .mp4, .mov, .avi, .wmv, .flv, .mkv, .webm, .mpeg, .mpg, .m4v files are accepted
                  </span>
                  <input
                    type="file"
                    multiple
                    accept=".doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.gif,.png,.pdf,.txt,.odt,.ods,.zip,.rar,.mp4,.mov,.avi,.wmv,.flv,.mkv,.webm,.mpeg,.mpg,.m4v"
                    className="hidden"
                    onChange={(e) => {
                      addFiles(e.target.files)
                      e.target.value = ''
                    }}
                  />
                </label>
                {attachments.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {attachments.map((f, i) => (
                      <AttachmentThumb
                        key={`${f.name}-${i}`}
                        file={f}
                        onRemove={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Tags</label>
                <div className="relative">
                  <Tag className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag() } }}
                    placeholder="Add a tag and press Enter"
                    className={`${inputClass} border-slate-200 pl-9`}
                  />
                </div>
                {tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {tags.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 rounded-[3px] bg-blue-50 px-2 py-1 text-xs font-medium text-[#3366FF]">
                        {t}
                        <button type="button" onClick={() => removeTag(t)} className="ml-0.5 text-[#3366FF]/60 hover:text-[#3366FF]"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </FormSection>

            {/* ── RIGHT: Conversation ── */}
            <FormSection icon={MessageSquare} title="Conversation" hint="Routing and message">
              <Field label="Campaign" required error={errors.campaignId}>
                <SelectField icon={<Building2 className="h-4 w-4" />} value={campaignId} onChange={handleCampaignChange} placeholder="Select Campaign" error={errors.campaignId} options={campaignOptions} />
              </Field>
              <Field label="Team" required error={errors.teamId}>
                <SelectField icon={<Users className="h-4 w-4" />} value={teamId} onChange={handleTeamChange} disabled={!campaignId} placeholder={campaignId ? 'Select Team' : 'Select a campaign first'} error={errors.teamId} options={teamOptions} />
              </Field>
              <Field label="Agent">
                <SelectField icon={<UserCog className="h-4 w-4" />} value={agentId} onChange={setAgentId} disabled={!campaignId} placeholder={campaignId ? 'Select Agent' : 'Select a campaign first'} options={agentOptions} />
              </Field>
              {/* Additional properties, conversation properties, and separators in the admin-defined order. */}
              {orderedItems.map((item, i) =>
                item.type === 'separator' ? (
                  <div key={`sep-${i}`} className="flex items-center gap-3 pt-1">
                    <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{item.label}</span>
                    <span className="h-px flex-1 bg-slate-200" />
                  </div>
                ) : item.type === 'fixed' ? (
                  renderFixedField(item.key)
                ) : item.type === 'core' ? (
                  item.key === 'subject' ? (
                    <Field key="subject" label="Subject" required error={errors.subject}>
                      <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Enter a subject line" className={`${inputClass} ${errors.subject ? 'border-red-300' : 'border-slate-200'}`} />
                    </Field>
                  ) : (
                    <Field key="description" label="Description" required error={errors.message}>
                      <RichTextEditor value={message} onChange={setMessage} height={220} placeholder="Describe the interaction (minimum 10 characters)…" />
                    </Field>
                  )
                ) : item.type === 'prop' ? (
                  item.prop.inputType === 'checkbox' ? (
                    <div key={item.prop.columnName}>
                      <AdditionalPropertyField
                        prop={item.prop}
                        value={propValues[item.prop.columnName]}
                        onChange={(v) => { setPropValues((prev) => ({ ...prev, [item.prop.columnName]: v })) }}
                        {...getGeoCascadeProps(item.prop)}
                      />
                      {errors[`prop_${item.prop.columnName}`] && (
                        <p className="mt-1 text-xs text-red-500">{errors[`prop_${item.prop.columnName}`]}</p>
                      )}
                    </div>
                  ) : (
                    <Field key={item.prop.columnName} label={item.prop.displayName} required={item.prop.isRequired} error={errors[`prop_${item.prop.columnName}`]}>
                      <AdditionalPropertyField
                        prop={item.prop}
                        value={propValues[item.prop.columnName]}
                        onChange={(v) => {
                          setPropValues((prev) => ({ ...prev, [item.prop.columnName]: v }))
                          // Trigger cascade fetch for child geo properties
                          const role = getGeoRole(item.prop)
                          const strVal = v === null || v === undefined ? '' : String(v)
                          if (strVal && role === 'country' && geoProps.state) {
                            void fetchCascadeOptions(geoProps.state.id, strVal)
                          } else if (strVal && role === 'state' && geoProps.city) {
                            void fetchCascadeOptions(geoProps.city.id, strVal)
                          }
                        }}
                        {...getGeoCascadeProps(item.prop)}
                      />
                    </Field>
                  )
                ) : null,
              )}
            </FormSection>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-8 py-4">
          <span className="hidden text-xs sm:block">
            {submitError ? (
              <span className="font-medium text-red-500">{submitError}</span>
            ) : (
              <span className="text-slate-400">
                <span className="text-red-500">*</span> Client, Campaign, Team, Subject &amp; Description are required
              </span>
            )}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={submitting}
              className="rounded-[3px] px-5 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => void handleSubmit()}
              disabled={submitting || uploadingAttachments}
              className="flex items-center gap-2 rounded-[3px] bg-[#3366FF] px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#2952CC] hover:shadow-md active:scale-[0.98] disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  Creating…
                </>
              ) : uploadingAttachments ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  Uploading…
                </>
              ) : isChild ? (
                <>
                  <Network className="h-4 w-4" />
                  Create Child Conversation
                </>
              ) : (
                <>
                  <MessageSquarePlus className="h-4 w-4" />
                  Create Conversation
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Client search dialog */}
      <ClientDialog
        open={clientDialogOpen}
        onClose={() => setClientDialogOpen(false)}
        selectedId={clientId}
        onSelect={(c) => { setClientId(c.id); setClientName(c.full_name || c.email || 'Client'); setClientEmail(c.email || '') }}
      />
    </>
  )
}
